import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, Mail, ClipboardList, CreditCard, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ServicesEditor } from "@/components/dashboard/ServicesEditor";
import { SquareCardForm } from "@/components/dashboard/SquareCardForm";
import { api } from "@/lib/api";
import { DONE_FOR_YOU_FEE } from "@/components/pricingData";
import { toast } from "sonner";

const INCLUDES = [
  "AI Receptionist creation", "Business profile configuration", "Services & pricing setup",
  "FAQs and knowledge base", "Business hours", "Greeting customisation", "Voice configuration",
  "Multi-language setup", "Calendar integration (if connected)", "Testing before activation",
];
const STEPS_NEXT = [
  "Complete your payment.", "Fill in a short business questionnaire.",
  "The OperaMind team configures your AI Receptionist.", "You'll receive an email once it's ready for review.",
  "Review your AI in Test Mode.", "Click Activate AI Receptionist to start your 3-day trial.",
];

export default function DoneForYou() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("summary"); // summary | payment | questionnaire | done
  const [config, setConfig] = useState(null);
  const [pm, setPm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [serviceList, setServiceList] = useState([]);

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/payment-methods").then((r) => setPm(r.data.payment_method)).catch(() => {});
    api.get("/business").then((r) => { setCfg(r.data); if (r.data.service_list?.length) setServiceList(r.data.service_list); }).catch(() => {});
    api.get("/subscription").then((r) => {
      if (r.data.setup_purchased) setStage(r.data.questionnaire_completed ? "done" : "questionnaire");
    }).catch(() => {});
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      await api.post("/billing/done-for-you");
      toast.success("Payment received — thank you!");
      setStage("questionnaire");
    } catch (e) { toast.error(e.response?.data?.detail || "Payment could not be processed"); }
    finally { setBusy(false); }
  };
  const addCardAndPay = async (token) => {
    setBusy(true);
    try { await api.post("/payment-methods", { source_id: token }); await api.post("/billing/done-for-you"); toast.success("Payment received — thank you!"); setStage("questionnaire"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not process payment"); }
    finally { setBusy(false); }
  };
  const submitQuestionnaire = async () => {
    setBusy(true);
    try {
      const clean = serviceList.filter((s) => s.name?.trim());
      await api.post("/business/questionnaire", { ...cfg, service_list: clean });
      setStage("done");
    } catch (e) { toast.error("Could not submit — please check your details"); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6"><span className="font-heading text-xl font-extrabold">Opera<span className="text-blue-600">Mind</span></span></div>

        {stage === "summary" && (
          <div data-testid="dfy-summary" className="bg-white rounded-2xl border border-slate-200 p-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 text-xs font-bold px-3 py-1"><Sparkles className="h-3.5 w-3.5" /> CONCIERGE SETUP</div>
            <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight">Done-For-You Setup</h1>
            <p className="text-slate-500 mt-1"><span className="font-heading text-2xl font-bold text-slate-900">£{DONE_FOR_YOU_FEE}</span> one-time setup</p>
            <p className="mt-4 text-slate-600">Our team configures everything for you, including:</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {INCLUDES.map((i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-emerald-600 shrink-0" /> {i}</div>)}
            </div>
            <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-5">
              <p className="font-semibold text-slate-900 mb-3">What's next?</p>
              <ol className="space-y-2">
                {STEPS_NEXT.map((s, i) => <li key={i} className="flex gap-3 text-sm text-slate-600"><span className="h-5 w-5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>{s}</li>)}
              </ol>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button data-testid="dfy-continue-payment" onClick={() => setStage("payment")} className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 gap-2">Continue to payment <ArrowRight className="h-4 w-4" /></Button>
              <Button data-testid="dfy-back-self" onClick={() => navigate("/onboarding")} variant="outline" className="h-11 gap-2"><ArrowLeft className="h-4 w-4" /> Back to self-setup</Button>
            </div>
          </div>
        )}

        {stage === "payment" && (
          <div data-testid="dfy-payment" className="bg-white rounded-2xl border border-slate-200 p-8">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><CreditCard className="h-6 w-6 text-blue-600" /> Complete payment</h1>
            <p className="text-slate-500 mt-1">One-time Done-For-You setup — <span className="font-semibold text-slate-900">£{DONE_FOR_YOU_FEE}</span></p>
            <div className="mt-5">
              {pm ? (
                <>
                  <div className="rounded-lg border border-slate-200 p-4 text-sm flex items-center justify-between"><span>{pm.brand} •••• {pm.last4}</span><span className="text-emerald-600 font-semibold">On file</span></div>
                  <Button data-testid="dfy-pay-btn" onClick={pay} disabled={busy} className="w-full mt-4 h-11 bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay £${DONE_FOR_YOU_FEE}`}</Button>
                </>
              ) : config?.provider === "square" && config ? (
                <SquareCardForm config={config} onToken={addCardAndPay} busy={busy} />
              ) : (
                <Button data-testid="dfy-pay-btn" onClick={pay} disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay £${DONE_FOR_YOU_FEE}`}</Button>
              )}
            </div>
            <button onClick={() => setStage("summary")} className="mt-4 text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
            <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Square. Sandbox test card: 4111 1111 1111 1111.</p>
          </div>
        )}

        {stage === "questionnaire" && cfg && (
          <div data-testid="dfy-questionnaire" className="bg-white rounded-2xl border border-slate-200 p-8">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><ClipboardList className="h-6 w-6 text-blue-600" /> Business questionnaire</h1>
            <p className="text-slate-500 mt-1">Tell us about your business so our team can build your AI accurately.</p>
            <div className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Business name</Label><Input data-testid="q-name" value={cfg.name || ""} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Industry / type</Label><Input data-testid="q-industry" value={cfg.industry || ""} onChange={(e) => set("industry", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Opening hours</Label><Input data-testid="q-hours" value={cfg.hours || ""} onChange={(e) => set("hours", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Phone</Label><Input data-testid="q-phone" value={cfg.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Greeting</Label><Textarea data-testid="q-greeting" value={cfg.greeting || ""} onChange={(e) => set("greeting", e.target.value)} rows={2} className="mt-1" /></div>
              <div>
                <Label className="text-xs">Services &amp; pricing</Label>
                <div className="mt-2"><ServicesEditor value={serviceList} onChange={setServiceList} /></div>
              </div>
              <Button data-testid="q-submit" onClick={submitQuestionnaire} disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-700 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit questionnaire <ArrowRight className="h-4 w-4" /></>}</Button>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div data-testid="dfy-done" className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mx-auto"><PartyPopper className="h-8 w-8" /></div>
            <h1 className="mt-5 font-heading text-2xl font-extrabold tracking-tight">You're all set!</h1>
            <p className="text-slate-600 mt-2 max-w-md mx-auto">Thank you — payment received and your questionnaire is submitted. Our team is now building your AI Receptionist.</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-50 text-blue-700 text-sm px-4 py-2"><Mail className="h-4 w-4" /> We'll email you the moment it's ready to review in Test Mode.</div>
            <div className="mt-6"><Button data-testid="dfy-go-dashboard" onClick={() => navigate("/dashboard")} className="h-11 px-6 bg-blue-600 hover:bg-blue-700">Go to dashboard</Button></div>
          </div>
        )}
      </div>
    </div>
  );
}
